import {
	ActionListBusyIndicator,
	BusyIndicator,
	DropOverActionList,
	Icon,
	ListItem,
	Portal
} from "client/Components";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	BookmarkModel,
	getSharePointModels,
	getWebDavModels,
	SharePointModels
} from "client/Api/Documents/Bookmarks/bookmarks";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";
import {
	OpenBookmarkPossibilitiesProvider,
	OpenPossibilities
} from "client_legacy/Documents/Bookmarks/bookmarkOpenPossibilities";
import { Opener } from "client_legacy/Documents/Bookmarks/opener";
import { DocumentStoreType, OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	getDocumentKind,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/fileExtensions";
import {
	getOpenLocalizationsForExtension,
	OpenLocalizations
} from "client_legacy/Documents/openLocalizations";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	BookmarksProvider,
	InterestedBookmarkParty
} from "client_legacy/Workspace/LiveInformation/Documents/provider";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import * as sharePointDocumentOpener from "client_legacy/Documents/SharePoint/documentOpener";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { openShareALinkDialog } from "client/Features/Documents/SharePoint/Dialogs/ShareLink/ShareALinkDialog";
import {
	DocumentItemType,
	DocumentsViewType,
	DocumentItemViewModel
} from "client/Api/Documents/SharePoint/common";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { getFileDownloadUrl } from "client/Features/Documents/WebDav/actions";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { sendSharePointFileAsAttachment, sendWebDavFileAsAttachment } from "client_legacy/Email/SendFile/sendFile";
import * as downloadAction from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { download } from "client_legacy/ndaw/fileDownloader";
import { WebDavModels } from "client/Api/Documents/WebDav/webDav";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import {
	Action,
	ActionAndOthers,
	ActionGroup,
	ActionKind
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { actionComponentFactory, computeActionsToRender } from "client/Components/ActionList/actionsRenderHelpers";
import { ActionListNoResults } from "client/Components/ActionList/ActionListNoResults";
import { never } from "client_legacy/ndaw/errors";
import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { useEditorPreference } from "client/Features/Documents/useEditorPreference";

export type BookmarksTabProps = {
	appIcon: AppImageDisplayInfo;
	tabSelector: ReactElement;
	tabId: Guid;
	destinationWindow: DestinationWindow;
	provider: BookmarksProvider;
	addAnalyticsEvent(): void;
};

export const BookmarksTab = (props: BookmarksTabProps): ReactElement => {
	const {
		appIcon,
		tabSelector,
		tabId,
		destinationWindow,
		provider,
		addAnalyticsEvent
	} = props;

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			navigateRelativeToEnvironment(
				"/Documents?store=Bookmarks",
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[addAnalyticsEvent, destinationWindow]
	);

	return (
		<>
			<LiveTileContent.Header icon={appIcon}>
				{tabSelector}
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<BookmarkContent
					tabId={tabId}
					provider={provider}
					onAddAnalyticsEvent={addAnalyticsEvent}
					destinationWindow={destinationWindow}
				/>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_Bookmarks")}
				onClick={navigateHandler}
			/>
		</>
	);
};
BookmarksTab.displayName = "BookmarksTab";

type BookmarkContentProps = {
	tabId: Guid;
	provider: BookmarksProvider;
	onAddAnalyticsEvent(): void;
	destinationWindow: DestinationWindow;
};

const BookmarkContent = (props: BookmarkContentProps): ReactElement => {
	const {
		tabId,
		provider,
		onAddAnalyticsEvent,
		destinationWindow
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const editorPreference: UserEditorPreference = useEditorPreference();

	useEffect(
		(): (() => void) => {
			const party: InterestedBookmarkParty = {
				partyId: tabId,
				resourceId: undefined,
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState(({
						isLoading: false,
						hasGlobalFailure: true
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState(({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
				update: (items): void => {
					setLoadingState(({
						isLoading: false,
						items
					}));
				}
			};

			setLoadingState(({
				isLoading: true
			}));

			provider.register(party);

			return () => {
				provider.unregister(party);
			};
		},
		[provider, tabId]
	);

	const renderContent = (): ReactElement | null => {
		if (loadingState.isLoading) {
			return null;
		}

		if (loadingState.hasGlobalFailure) {
			return (
				<LiveTileContent.Message
					text={localize("_WeWereUnableToRetrieveTheItems")}
				/>
			);
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return (
				<TileRefresh onRefresh={() => provider.forceReload()} />
			);
		}

		const { items } = loadingState;

		if (items.length === 0) {
			return (
				<LiveTileContent.Message
					text={localize("_YouDoNotHaveBookmarkedFilesOrFolders")}
				/>
			);
		}

		return (
			<LiveTileContent.List trackingId="bookmark">
				{items.map((x): ReactElement => (
					<BookmarkListItem
						key={x.id}
						onAddAnalyticsEvent={onAddAnalyticsEvent}
						destinationWindow={destinationWindow}
						item={x}
						editorPreference={editorPreference}
					/>
				))}
			</LiveTileContent.List>
		);
	};

	return (
		<LiveTileContent.ContentStack>
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}
				<LiveTileContent.Scrollable>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
BookmarkContent.displayName = "BookmarkContent";

type LoadingState = {
	isLoading: true;
} | (
	{
		isLoading: false;
	}
	&
	(
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
			items?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
			items?: undefined;
		}
		|
		{
			items: readonly BookmarkModel[];
			hasGlobalFailure?: undefined;
			hasTooManyConsecutiveErrors?: undefined;
		}
	)
);

type BookmarkListItemProps = {
	onAddAnalyticsEvent: () => void;
	item: BookmarkModel;
	destinationWindow: DestinationWindow;
	editorPreference: UserEditorPreference;
};

const BookmarkListItem = (props: BookmarkListItemProps): ReactElement => {
	const {
		destinationWindow,
		item,
		onAddAnalyticsEvent,
		editorPreference
	} = props;

	const [isActionsMenuOpen, setIsActionsMenuOpen] = useState<boolean>(false);
	const [isOpeningItem, setIsOpeningItem] = useState<boolean>(false);
	const actionRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const opener = useMemo(
		(): Opener => {
			return new Opener(
				{
					onOpenFolderRequestStart: () => setIsOpeningItem(true),
					onOpenFolderRequestEnd: () => setIsOpeningItem(false),
					onOpenFileRequestStart: () => setIsOpeningItem(true),
					onOpenFileRequestEnd: () => setIsOpeningItem(false)
				},
				alertManager
			);
		},
		[alertManager]
	);

	const clickHandler = useCallback(
		async (): Promise<void> => {
			onAddAnalyticsEvent();
			setIsOpeningItem(true);

			try {
				const actions: readonly DetailsAction[] = await listActions(
					item,
					opener,
					alertManager,
					destinationWindow,
					editorPreference,
					globalCancellation
				);

				await actions[0].execute(undefined);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setIsOpeningItem(false);
		},
		[
			onAddAnalyticsEvent,
			item,
			opener,
			alertManager,
			destinationWindow,
			globalCancellation,
			editorPreference
		]
	);

	return (
		<>
			{isOpeningItem && (
				<Portal container={document.body}>
					<BusyIndicator />
				</Portal>
			)}
			<ListItem
				noMobileBorderBottom
				trackingId={generateTrackingId(`${item.id}`)}
			>
				<ListItem.Content
					onClick={clickHandler}
					thumbnail={
						<Icon
							value={{
								kind: "svg",
								name: getDocumentKind(item.itemType, item.extension)
							}}
							size="extra-extra-large"
						/>
					}
				>
					<ListItem.Title>
						{item.name}
					</ListItem.Title>
				</ListItem.Content>
				<ListItem.Action
					icon="ellipsis"
					onClick={() => setIsActionsMenuOpen(true)}
					ref={actionRef}
					trackingId="actions"
					isActive={isActionsMenuOpen}
				/>

				{isActionsMenuOpen && (
					<ActionMenu
						target={ensureNotNil(actionRef.current)}
						onClose={() => setIsActionsMenuOpen(false)}
						item={item}
						alertManager={alertManager}
						destinationWindow={destinationWindow}
						opener={opener}
						onAddAnalyticsEvent={onAddAnalyticsEvent}
						editorPreference={editorPreference}
					/>
				)}
			</ListItem>
		</>
	);
};
BookmarkListItem.displayName = "BookmarkListItem";

type ActionMenuProps = {
	onClose(): void;
	onAddAnalyticsEvent: () => void;
	item: BookmarkModel;
	destinationWindow: DestinationWindow;
	opener: Opener;
	alertManager: AlertManager;
	target: HTMLElement;
	editorPreference: UserEditorPreference;
};

const ActionMenu = (props: ActionMenuProps): ReactElement => {
	const {
		onClose,
		onAddAnalyticsEvent,
		alertManager,
		opener,
		target,
		item,
		destinationWindow,
		editorPreference
	} = props;

	const [loadingState, setLoadingState] = useState<DetailsLoadingState>({ kind: "loading" });

	useEffect(
		(): (() => void) => {
			const computeActions = async (bookmark: BookmarkModel, signal: AbortSignal): Promise<void> => {
				setLoadingState({ kind: "loading" });

				try {
					const actions: readonly DetailsAction[] = await listActions(
						bookmark,
						opener,
						alertManager,
						destinationWindow,
						editorPreference,
						signal
					);

					setLoadingState({
						kind: "success",
						actions
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setLoadingState({ kind: "error" });
				}
			};

			const cancellation = new AbortController();
			computeActions(item, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			alertManager,
			destinationWindow,
			item,
			opener,
			onClose,
			editorPreference
		]
	);
	const actionsToRender = useMemo(
		(): readonly ActionAndOthers<undefined>[] => {
			if (loadingState.kind !== "success") {
				return [];
			}

			return computeActionsToRender(
				loadingState.actions
					.map(
						(x): DetailsAction => ({
							...x,
							execute: (context) => {
								onAddAnalyticsEvent();
								x.execute(context);
							}
						})
					),
				undefined,
				true
			);
		},
		[loadingState, onAddAnalyticsEvent]
	);

	const renderContent = (): ReactElement => {
		switch (loadingState.kind) {
			case "loading":
				return (
					<ActionListBusyIndicator trackingId="loading" />
				);
			case "error":
				return (
					<ActionListNoResults
						trackingId="error"
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
					/>
				);
			case "success":
				if (actionsToRender.length === 0) {
					return (
						<ActionListNoResults
							trackingId="no-items"
							icon="list"
							title={localize("_NoItemsToShow")}
							message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
						/>
					);
				}

				return (
					<>
						{actionsToRender.map((x) => (
							<Fragment key={x.name}>
								{actionComponentFactory(x, undefined)}
							</Fragment>
						))}
					</>
				);
			default:
				never(loadingState);
		}
	};

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			targetAlignment="br"
			contentAlignment="tr"
			menuTitle={item.name}
		>
			{renderContent()}
		</DropOverActionList>
	);
};
ActionMenu.displayName = "ActionMenu";

type DetailsAction = Action<undefined>;

type DetailsLoadingState = {
	kind: "loading" | "error";
} | {
	kind: "success";
	actions: readonly DetailsAction[];
};

function guardAgainstInfectedDocument(
	document: DocumentItemViewModel,
	alertManager: AlertManager,
	handler: () => (Promise<void> | void)
): (() => Promise<void>) {
	return async (): Promise<void> => {
		if (document.itemType === DocumentItemType.File
			&& document.isInfected
		) {
			alertManager.showWarning(
				localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile")
			);
			return;
		}

		await handler();
	};
}

function createOpenLocationAction(
	opener: Opener,
	bookmark: BookmarkModel,
	opensInNewWindow: boolean
): DetailsAction {
	return {
		kind: ActionKind.Regular,
		name: "open-location",
		title: localize("_OpenLocation"),
		icon: "folder-open",
		canExecute: () => true,
		execute: async () => await opener.openLocation(bookmark, opensInNewWindow)
	};
};

const openGroup: ActionGroup = {
	kind: "virtual",
	name: "open-group"
};

async function listActions(
	bookmark: BookmarkModel,
	opener: Opener,
	alertManager: AlertManager,
	destinationWindow: DestinationWindow,
	editorPreference: UserEditorPreference,
	signal: AbortSignal
): Promise<readonly DetailsAction[]> {
	const opensInNewWindow: boolean = destinationWindow === DestinationWindow.NewWindow;

	const openBookmarkPossibilitiesProvider = new OpenBookmarkPossibilitiesProvider();
	const openPossibilities: OpenPossibilities = openBookmarkPossibilitiesProvider.get(bookmark);

	const extension: string = bookmark.itemType === DocumentItemType.OneNoteNotebook
		? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
		: bookmark.extension;

	const openLocalization: OpenLocalizations = getOpenLocalizationsForExtension(extension);

	const result: DetailsAction[] = [];

	const defaultEditor: OfficeDocumentEditor = getDefaultEnvironmentOfficeDocumentEditor(bookmark);

	const officeActions: readonly DetailsAction[] = getItemsInPreferredOrder<DetailsAction>(
		defaultEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => {
					return openPossibilities.wopi
						? {
							kind: ActionKind.Regular,
							group: openGroup,
							name: "open-file-in-wopi",
							title: openLocalization.online.action,
							icon: "cloud",
							canExecute: () => true,
							execute: async () => await opener.openFileInWopi(bookmark)
						}
						: null;
				}
			],
			[
				OfficeDocumentEditor.Local,
				() => {
					return openPossibilities.local
						? {
							kind: ActionKind.Regular,
							name: "open-file-local",
							group: openGroup,
							title: openLocalization.local.action,
							icon: "display",
							canExecute: () => true,
							execute: async () => await opener.openFileLocal(bookmark, openPossibilities.wopi)
						}
						: null;
				}
			]
		],
		editorPreference
	);

	result.push(...officeActions);

	if (openPossibilities.browser) {
		result.push({
			kind: ActionKind.Regular,
			name: "open-file-in-browser",
			group: openGroup,
			title: openLocalization.online.action,
			icon: "cloud",
			canExecute: () => true,
			execute: async () => await opener.openFileInline(bookmark)
		});
	}

	if (openPossibilities.imagePreviewDialog) {
		result.push({
			kind: ActionKind.Regular,
			name: "open-in-image-preview-dialog",
			group: openGroup,
			title: openLocalization.online.action,
			icon: "cloud",
			canExecute: () => true,
			execute: async () => await opener.openInImagePreviewDialog(bookmark)
		});
	}

	if (openPossibilities.externalUrl) {
		result.push({
			kind: ActionKind.Regular,
			name: "open-url-file",
			group: openGroup,
			title: openLocalization.online
				.action,
			icon: "folder-open",
			canExecute: () => true,
			execute: async () => await opener.openUrlFile(bookmark)
		});
	}

	if (openPossibilities.folder) {
		result.push({
			kind: ActionKind.Regular,
			name: "open-folder",
			group: openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: () => true,
			execute: async () => await opener.openFolder(bookmark, opensInNewWindow)
		});
	}

	if (bookmark.storeType === DocumentStoreType.SharePoint) {
		const model: SharePointModels = await getSharePointModels(bookmark.id, signal);

		if (model.item.itemType !== DocumentItemType.Folder) {

			if (openPossibilities.sharePointViewer) {
				result.push({
					kind: ActionKind.Regular,
					name: "open-in-online-viewer",
					group: openGroup,
					title: localize("_OpenInAppName")
						.replace(
							"{0}",
							localize("_OnlineViewer")
						),
					icon: "folder-open",
					canExecute: () => true,
					execute: async () => {
						const {
							item
						} = model;

						if (item.itemType !== DocumentItemType.File) {
							throw new Error("The document is not a file.");
						}

						await guardAgainstInfectedDocument(
							item,
							alertManager,
							async () => {
								await sharePointDocumentOpener
									.openFileInSharePointViewer(item);
							}
						)();
					}
				});
			}

			if (openPossibilities.location) {
				result.push(createOpenLocationAction(
					opener,
					bookmark,
					opensInNewWindow
				));
			}

			if (
				documentsConfiguration().sharePoint
					.isSharingViaLinkEnabled
				&& model.view.type !== DocumentsViewType.SharedWithMeView
			) {
				result.push({
					kind: ActionKind.Regular,
					name: "share-link",
					title: localize("_ShareLink"),
					icon: "link",
					canExecute: () => true,
					execute: guardAgainstInfectedDocument(
						model.item,
						alertManager,
						() => openShareALinkDialog({ document: model.item })
					)
				});
			}

			if (model.item
				.permissions
				.canDownload
			) {
				result.push({
					kind: ActionKind.Regular,
					name: "download",
					title: localize("_Download"),
					icon: "download",
					canExecute: () => true,
					execute: guardAgainstInfectedDocument(
						model.item,
						alertManager,
						() => downloadAction.downloadDocument(model.item)
					)
				});

				const emailClient: EmailClient = getEffectiveEmailClient();
				if (emailClient.supportsEditDraft) {
					result.push({
						kind: ActionKind.Regular,
						name: "attach-to-email",
						title: localize("_AttachToEmail"),
						icon: "envelope",
						canExecute: () => true,
						execute: guardAgainstInfectedDocument(
							model.item,
							alertManager,
							() => sendSharePointFileAsAttachment(
								model.item.id,
								convertToLocationModel(model.item.location),
								emailClient,
								"#main-content"
							)
						)
					});
				}
			}
		}
	}

	if (bookmark.storeType === DocumentStoreType.WebDav) {
		const model: WebDavModels = await getWebDavModels(bookmark.id, signal);
		const {
			item,
			server
		} = model;

		if (openPossibilities.location) {
			result.push(createOpenLocationAction(
				opener,
				bookmark,
				opensInNewWindow
			));
		}

		if (!item.isFolder) {
			result.push({
				kind: ActionKind.Regular,
				name: "download",
				title: localize("_Download"),
				icon: "download",
				canExecute: () => true,
				execute: () => download(getFileDownloadUrl(item.id, server.id))
			});

			const emailClient: EmailClient = getEffectiveEmailClient();
			if (emailClient.supportsEditDraft) {
				result.push({
					kind: ActionKind.Regular,
					name: "attach-to-email",
					title: localize("_AttachToEmail"),
					icon: "envelope",
					canExecute: () => true,
					execute: () => {
						sendWebDavFileAsAttachment(
							server.id,
							item.id,
							emailClient
						);
					}
				});
			}
		}
	}

	return result;
}

function getDefaultEnvironmentOfficeDocumentEditor(bookmark: BookmarkModel): OfficeDocumentEditor {
	const { sharePoint, webDav } = documentsConfiguration();

	switch (bookmark.storeType) {
		case DocumentStoreType.WebDav:
			return webDav
				? webDav.defaultOfficeDocumentEditor
				: OfficeDocumentEditor.Online;
		case DocumentStoreType.SharePoint:
			return sharePoint.defaultOfficeDocumentEditor;
		default:
			never(bookmark);
	}
}
